import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Users, UtensilsCrossed, Dumbbell, CalendarDays,
  MessageSquare, Euro, ChevronRight, ArrowUpRight,
  Zap, Plus, Camera, AlertTriangle, Sparkles, Loader2,
  ClipboardList, UserX, Clock, Star, Activity, TrendingDown,
} from "lucide-react";
import PlatformReviewDialog from "@/components/PlatformReviewDialog";
import DisciplineBadge from "@/components/training/DisciplineBadge";
import CoachBadgesPanel from "@/components/badges/CoachBadgesPanel";

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Bonjour";
  if (h < 18) return "Bon après-midi";
  return "Bonsoir";
}

function StatCardSkeleton() {
  return (
    <Card className="border-slate-100">
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-7 w-12" />
          </div>
          <Skeleton className="w-11 h-11 rounded-xl" />
        </div>
      </CardContent>
    </Card>
  );
}

function StudentRowSkeleton() {
  return (
    <div className="flex items-center gap-3 p-3">
      <Skeleton className="w-9 h-9 rounded-full flex-shrink-0" />
      <div className="space-y-1.5 flex-1">
        <Skeleton className="h-3.5 w-28" />
        <Skeleton className="h-3 w-36" />
      </div>
    </div>
  );
}

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function getWeekStart() {
  const d = new Date();
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
}

function formatSessionDate(date, time) {
  if (!date) return "";
  try {
    const d = new Date(`${date}T${time || "00:00"}`);
    return (
      d.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" }) +
      (time ? ` à ${time.slice(0, 5)}` : "")
    );
  } catch {
    return `${date}${time ? " à " + time : ""}`;
  }
}

export default function CoachDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [allSessions, setAllSessions] = useState([]);
  const [checkinOverview, setCheckinOverview] = useState([]);
  const [trainingAdherence, setTrainingAdherence] = useState([]);
  const [requestingPhoto, setRequestingPhoto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [platformReviewOpen, setPlatformReviewOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get('/dashboard/stats').catch(() => ({ data: null })),
      api.get('/students').catch(() => ({ data: [] })),
      api.get('/coaching-sessions').catch(() => ({ data: [] })),
      api.get('/inactivity/alerts?ai=1').catch(() => ({ data: { alerts: [] } })),
      api.get('/checkins/overview').catch(() => ({ data: [] })),
      api.get('/workouts/adherence/overview?days=30').catch(() => ({ data: [] })),
    ]).then(([statsRes, studentsRes, sessionsRes, alertsRes, checkinsRes, adherenceRes]) => {
      setStats(statsRes.data);
      const studentList = studentsRes.data || [];
      setStudents(studentList.slice(0, 5));
      const sessionList = sessionsRes.data || [];
      setAllSessions(sessionList);
      setSessions(sessionList.filter(s => s.status === 'scheduled').slice(0, 5));
      setAlerts(alertsRes.data?.alerts || []);
      setCheckinOverview(checkinsRes.data || []);
      setTrainingAdherence(adherenceRes.data || []);
      setLoading(false);
    });
  }, []);

  const inactiveStudents = alerts.filter((a) => a.type === "inactive");
  const currentWeek = getWeekStart();
  const missingCheckins = checkinOverview.filter(
    (item) => !item.latest || item.latest.week_start !== currentWeek
  );
  const lowTrainingAdherence = trainingAdherence.filter(
    (row) => row.has_plan && (row.adherence_percent ?? 100) < 70
  );
  const todaySessions = allSessions
    .filter((s) => s.date === todayStr() && s.status === "scheduled")
    .sort((a, b) => (a.time || "").localeCompare(b.time || ""));

  const statCards = stats ? [
    { label: "Élèves", value: stats.students_count, icon: Users, color: "bg-blue-50 text-blue-600", accent: "border-l-blue-500" },
    { label: "Plans Diet", value: stats.diet_plans_count, icon: UtensilsCrossed, color: "bg-green-50 text-green-600", accent: "border-l-green-500" },
    { label: "Plans Training", value: stats.training_plans_count, icon: Dumbbell, color: "bg-orange-50 text-orange-600", accent: "border-l-orange-500" },
    { label: "Séances prévues", value: stats.sessions_count, icon: CalendarDays, color: "bg-purple-50 text-purple-600", accent: "border-l-purple-500" },
    { label: "Messages non lus", value: stats.unread_messages, icon: MessageSquare, color: "bg-pink-50 text-pink-600", accent: "border-l-pink-500" },
    {
      label: "Revenus nets",
      value: `${stats.total_revenue?.toFixed(2) ?? "0.00"}€`,
      sub: stats.gross_revenue > 0
        ? `Brut ${stats.gross_revenue?.toFixed(2)}€ · frais ${((stats.platform_fees ?? 0) + (stats.stripe_fees ?? 0)).toFixed(2)}€`
        : null,
      icon: Euro,
      color: "bg-emerald-50 text-emerald-600",
      accent: "border-l-emerald-500",
    },
  ] : [];

  const handlePhotoRequest = async (studentId) => {
    setRequestingPhoto(studentId);
    try {
      await api.post('/inactivity/photo-request', { student_id: studentId });
      toast.success("Demande de photos envoyée !");
      const res = await api.get('/inactivity/alerts');
      setAlerts(res.data?.alerts || []);
    } catch (err) {
      if (err.response?.status === 409) {
        toast.info("Une demande est déjà en cours pour cet élève");
      } else {
        toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
      }
    } finally {
      setRequestingPhoto(null);
    }
  };

  const quickActions = [
    { label: "Élèves", icon: Users, path: "/coach/students", color: "text-blue-600 bg-blue-50 hover:bg-blue-100" },
    { label: "Calendrier", icon: CalendarDays, path: "/coach/calendar", color: "text-purple-600 bg-purple-50 hover:bg-purple-100" },
    { label: "Training", icon: Dumbbell, path: "/coach/training-plans", color: "text-orange-600 bg-orange-50 hover:bg-orange-100" },
    { label: "Diet", icon: UtensilsCrossed, path: "/coach/diet-plans", color: "text-green-600 bg-green-50 hover:bg-green-100" },
  ];

  return (
    <div data-testid="coach-dashboard">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-blue-600 uppercase tracking-widest mb-1 flex items-center gap-1">
            <Zap className="w-3.5 h-3.5" />
            {getGreeting()}{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
          </p>
          <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">Dashboard</h1>
          <p className="text-slate-400 mt-1 text-sm">
            {new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </p>
        </div>
        <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPlatformReviewOpen(true)}
          className="gap-1.5 rounded-full"
        >
          <Star className="w-4 h-4 text-yellow-500" />
          Avis plateforme
        </Button>
        <button
          onClick={() => navigate('/coach/students')}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 h-10 rounded-full shadow-lg shadow-blue-500/20 transition-colors"
          data-testid="add-student-btn"
        >
          <Plus className="w-4 h-4" />
          Ajouter
        </button>
        </div>
      </div>

      <PlatformReviewDialog open={platformReviewOpen} onOpenChange={setPlatformReviewOpen} />

      {/* Quick Actions */}
      <div className="grid grid-cols-4 gap-3 mt-6">
        {quickActions.map((a, i) => (
          <button
            key={i}
            onClick={() => navigate(a.path)}
            className={`flex flex-col items-center justify-center gap-2 p-4 rounded-2xl font-semibold text-xs transition-colors ${a.color}`}
            data-testid={`quick-action-${i}`}
          >
            <a.icon className="w-5 h-5" />
            {a.label}
          </button>
        ))}
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        {loading
          ? Array.from({ length: 6 }).map((_, i) => <StatCardSkeleton key={i} />)
          : statCards.map((s, i) => (
            <Card key={i} className={`border-slate-100 border-l-4 ${s.accent} hover:shadow-md transition-shadow`} data-testid={`stat-card-${i}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wider font-medium">{s.label}</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{s.value ?? "—"}</p>
                    {s.sub && <p className="text-[10px] text-slate-400 mt-1 leading-tight">{s.sub}</p>}
                  </div>
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${s.color}`}>
                    <s.icon className="w-5 h-5" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        }
      </div>

      <div className="mt-6">
        <CoachBadgesPanel />
      </div>

      {/* Coach widgets row */}
      {!loading && (
        <div className="grid lg:grid-cols-3 gap-4 mt-6">
          {/* Today's sessions */}
          <Card className="border-l-4 border-l-purple-500 border-slate-100">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-5 h-5 text-purple-600" />
                <h3 className="font-heading text-sm font-semibold text-slate-900 uppercase tracking-tight">
                  Séances du jour
                </h3>
                <Badge className="bg-purple-100 text-purple-700 text-xs ml-auto">{todaySessions.length}</Badge>
              </div>
              {todaySessions.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">Aucune séance aujourd'hui</p>
              ) : (
                <div className="space-y-2">
                  {todaySessions.slice(0, 4).map((s) => (
                    <button
                      key={s.session_id}
                      type="button"
                      onClick={() => navigate("/coach/calendar")}
                      className="w-full flex items-center gap-3 p-2.5 rounded-xl bg-slate-50 hover:bg-purple-50/50 transition-colors text-left"
                    >
                      <span className="text-xs font-bold text-purple-600 w-12">{s.time?.slice(0, 5)}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{s.title}</p>
                        <p className="text-xs text-slate-400 truncate">{s.student_name}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Missing check-ins */}
          <Card className="border-l-4 border-l-amber-500 border-slate-100">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <ClipboardList className="w-5 h-5 text-amber-600" />
                <h3 className="font-heading text-sm font-semibold text-slate-900 uppercase tracking-tight">
                  Check-ins manquants
                </h3>
                <Badge className="bg-amber-100 text-amber-700 text-xs ml-auto">{missingCheckins.length}</Badge>
              </div>
              {missingCheckins.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">Tous les élèves ont check-in cette semaine ✓</p>
              ) : (
                <div className="space-y-2">
                  {missingCheckins.slice(0, 4).map((item) => (
                    <button
                      key={item.student_id}
                      type="button"
                      onClick={() => navigate(`/coach/students/${item.student_id}`)}
                      className="w-full flex items-center gap-3 p-2.5 rounded-xl bg-slate-50 hover:bg-amber-50/50 transition-colors text-left"
                    >
                      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-xs font-bold text-amber-700 flex-shrink-0">
                        {item.student_name?.[0]?.toUpperCase() || "?"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{item.student_name}</p>
                        <p className="text-xs text-amber-600">Pas de check-in cette semaine</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Inactive students */}
          <Card className="border-l-4 border-l-red-500 border-slate-100">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <UserX className="w-5 h-5 text-red-600" />
                <h3 className="font-heading text-sm font-semibold text-slate-900 uppercase tracking-tight">
                  Élèves inactifs
                </h3>
                <Badge className="bg-red-100 text-red-700 text-xs ml-auto">{inactiveStudents.length}</Badge>
              </div>
              {inactiveStudents.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">Tous vos élèves sont actifs ✓</p>
              ) : (
                <div className="space-y-2">
                  {inactiveStudents.slice(0, 4).map((alert) => (
                    <button
                      key={alert.student.user_id}
                      type="button"
                      onClick={() => navigate(`/coach/students/${alert.student.user_id}`)}
                      className="w-full flex items-center gap-3 p-2.5 rounded-xl bg-slate-50 hover:bg-red-50/50 transition-colors text-left"
                    >
                      <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center text-xs font-bold text-red-700 flex-shrink-0">
                        {alert.student.name?.[0]?.toUpperCase() || "?"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{alert.student.name}</p>
                        <p className="text-xs text-slate-500 truncate">{alert.details}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Adhérence entraînement */}
      {!loading && trainingAdherence.length > 0 && (
        <Card className="mt-6 border-l-4 border-l-orange-500 border-slate-100">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Activity className="w-5 h-5 text-orange-600" />
              <h3 className="font-heading text-lg font-semibold text-slate-900 uppercase tracking-tight">
                Adhérence entraînement
              </h3>
              <Badge className="bg-orange-100 text-orange-700 text-xs ml-auto">30 jours</Badge>
            </div>
            <p className="text-xs text-slate-500 mb-4">
              Séances loguées vs fréquence prévue dans le programme actif de chaque élève.
            </p>
            <div className="space-y-3">
              {trainingAdherence.slice(0, 6).map((row) => {
                const pct = row.adherence_percent ?? 0;
                const barColor = pct >= 70 ? "bg-green-500" : pct >= 40 ? "bg-amber-500" : "bg-red-400";
                return (
                  <button
                    key={row.student_id}
                    type="button"
                    onClick={() => navigate(`/coach/students/${row.student_id}`)}
                    className="w-full flex items-center gap-4 p-3 rounded-xl bg-slate-50 hover:bg-orange-50/40 transition-colors text-left"
                    data-testid={`training-adherence-${row.student_id}`}
                  >
                    <div className="w-9 h-9 rounded-full bg-orange-100 flex items-center justify-center text-sm font-bold text-orange-700 flex-shrink-0">
                      {row.student_name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{row.student_name}</p>
                      <p className="text-xs text-slate-500 truncate">
                        {row.has_plan
                          ? `${row.sessions_logged}/${row.sessions_expected} séances · ${row.plan_title || "Programme"}`
                          : "Aucun programme assigné"}
                        {row.days_since_last != null && row.days_since_last > 7 && (
                          <span className="text-amber-600 ml-1">· {row.days_since_last}j sans séance</span>
                        )}
                      </p>
                      {row.plan_discipline && (
                        <div className="mt-1">
                          <DisciplineBadge discipline={row.plan_discipline} size="sm" />
                        </div>
                      )}
                      {row.has_plan && (
                        <div className="mt-2 h-1.5 bg-slate-200 rounded-full overflow-hidden max-w-xs">
                          <div className={`h-full ${barColor} rounded-full`} style={{ width: `${pct}%` }} />
                        </div>
                      )}
                    </div>
                    <Badge
                      variant={pct >= 70 ? "default" : "secondary"}
                      className={`flex-shrink-0 ${pct < 40 ? "bg-red-50 text-red-700" : pct < 70 ? "bg-amber-50 text-amber-700" : ""}`}
                    >
                      {row.has_plan ? `${pct}%` : "—"}
                    </Badge>
                  </button>
                );
              })}
            </div>
            {lowTrainingAdherence.length > 0 && (
              <p className="text-xs text-amber-600 mt-4 flex items-center gap-1">
                <TrendingDown className="w-3.5 h-3.5" />
                {lowTrainingAdherence.length} élève{lowTrainingAdherence.length > 1 ? "s" : ""} sous 70% d&apos;adhérence
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Alertes d'inactivité */}
      {!loading && alerts.length > 0 && (
        <Card className="mt-6 border-l-4 border-l-purple-500 border-purple-200/50">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-purple-600" />
              <h3 className="font-heading text-lg font-semibold text-slate-900 uppercase tracking-tight">Alertes d'inactivité</h3>
              <Badge className="bg-purple-100 text-purple-700 text-xs">{alerts.length}</Badge>
              <Sparkles className="w-4 h-4 text-purple-400 ml-1" />
            </div>
            <div className="space-y-3">
              {alerts.map((alert) => (
                <div key={alert.student.user_id} className="flex items-start gap-3 p-3 rounded-xl bg-slate-50 hover:bg-purple-50/30 transition-colors">
                  <div
                    className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-sm font-bold text-white flex-shrink-0 cursor-pointer"
                    onClick={() => navigate(`/coach/students/${alert.student.user_id}`)}
                  >
                    {alert.student.name?.[0]?.toUpperCase() ?? "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className="text-sm font-semibold text-slate-900 cursor-pointer hover:text-purple-600 transition-colors"
                        onClick={() => navigate(`/coach/students/${alert.student.user_id}`)}
                      >
                        {alert.student.name}
                      </span>
                      <Badge variant="outline" className={alert.type === 'inactive'
                        ? 'bg-red-50 text-red-700 border-red-200 text-[10px]'
                        : 'bg-amber-50 text-amber-700 border-amber-200 text-[10px]'}>
                        {alert.type === 'inactive' ? 'Inactif' : 'Stagnant'}
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{alert.details}</p>
                    {alert.suggestion && (
                      <p className="text-xs text-purple-600 mt-1 flex items-start gap-1">
                        <Sparkles className="w-3 h-3 flex-shrink-0 mt-0.5" />
                        <span className="italic">{alert.suggestion}</span>
                      </p>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={alert.has_pending_request || requestingPhoto === alert.student.user_id}
                    onClick={() => handlePhotoRequest(alert.student.user_id)}
                    className={`flex-shrink-0 gap-1.5 rounded-full text-xs ${
                      alert.has_pending_request
                        ? 'text-slate-400 border-slate-200'
                        : 'text-purple-600 border-purple-200 hover:bg-purple-50'
                    }`}
                  >
                    {requestingPhoto === alert.student.user_id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Camera className="w-3.5 h-3.5" />
                    )}
                    {alert.has_pending_request ? 'En cours' : 'Demander photos'}
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-6 mt-8">
        {/* Recent Students */}
        <Card className="border-slate-100">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading text-lg font-semibold text-slate-900 uppercase tracking-tight">Élèves récents</h3>
              <button
                onClick={() => navigate('/coach/students')}
                className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium"
                data-testid="view-all-students-btn"
              >
                Voir tout <ArrowUpRight className="w-3.5 h-3.5" />
              </button>
            </div>
            {loading ? (
              <div className="space-y-1">
                {Array.from({ length: 3 }).map((_, i) => <StudentRowSkeleton key={i} />)}
              </div>
            ) : students.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                  <Users className="w-5 h-5 text-slate-400" />
                </div>
                <p className="text-sm text-slate-400">Aucun élève pour le moment</p>
                <button onClick={() => navigate('/coach/students')} className="mt-3 text-sm text-blue-600 hover:underline font-medium">
                  Inviter un élève
                </button>
              </div>
            ) : (
              <div className="space-y-1">
                {students.map(s => (
                  <button
                    key={s.user_id}
                    onClick={() => navigate(`/coach/students/${s.user_id}`)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors text-left group"
                    data-testid={`student-item-${s.user_id}`}
                  >
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-sm font-bold text-white flex-shrink-0">
                      {s.name?.[0]?.toUpperCase() ?? "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{s.name}</p>
                      <p className="text-xs text-slate-400 truncate">{s.email}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 transition-colors flex-shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Sessions */}
        <Card className="border-slate-100">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading text-lg font-semibold text-slate-900 uppercase tracking-tight">Séances à venir</h3>
              <button
                onClick={() => navigate('/coach/calendar')}
                className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium"
                data-testid="view-all-sessions-btn"
              >
                Calendrier <ArrowUpRight className="w-3.5 h-3.5" />
              </button>
            </div>
            {loading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex gap-3 p-3">
                    <Skeleton className="w-9 h-9 rounded-xl flex-shrink-0" />
                    <div className="space-y-1.5 flex-1">
                      <Skeleton className="h-3.5 w-32" />
                      <Skeleton className="h-3 w-44" />
                    </div>
                  </div>
                ))}
              </div>
            ) : sessions.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                  <CalendarDays className="w-5 h-5 text-slate-400" />
                </div>
                <p className="text-sm text-slate-400">Aucune séance prévue</p>
                <button onClick={() => navigate('/coach/calendar')} className="mt-3 text-sm text-blue-600 hover:underline font-medium">
                  Planifier une séance
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {sessions.map(s => (
                  <div key={s.session_id} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-purple-50/50 transition-colors">
                    <div className="w-9 h-9 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <CalendarDays className="w-4 h-4 text-purple-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{s.title}</p>
                      <p className="text-xs text-slate-400">{formatSessionDate(s.date, s.time)} · {s.student_name || "N/A"}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
